import StringsFuncs from "./stringsFuncs";

export const formatCpfCnpj = (value: string): string => {
  if (value.length === 11) {
    // Formata CPF (XXX.XXX.XXX-XX)
    return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else if (value.length === 14) {
    // Formata CNPJ (XX.XXX.XXX/XXXX-XX)
    return value.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }
  return value; // Retorna sem formatação caso não se enquadre
};

export const formatCep = (value: string): string => {
  // Remove qualquer caractere não-numérico
  value = value.replace(/\D/g, "");

  if (value.length === 8) {
    // Formata CEP (XXXXX-XXX)
    return value.replace(/(\d{5})(\d{3})/, "$1-$2");
  }

  // Caso não tenha 8 dígitos, retorna o valor original sem formatação
  return value;
};

export const formatAddress = (
  address?: string,
  number?: string,
  unit?: string,
  zipcode?: string,
  neighborhood?: string,
  city?: string
): string => {
  var fullAddress = "";

  if (address) fullAddress = new StringsFuncs(address).cammelCase();

  if (number)
    fullAddress = fullAddress === "" ? number : `${fullAddress}, ${number}`;

  if (unit)
    fullAddress =
      fullAddress === ""
        ? new StringsFuncs(unit).firstLetters()
        : `${fullAddress} - ${new StringsFuncs(unit).capitalLetter()}`;

  if (zipcode)
    fullAddress =
      fullAddress === ""
        ? formatCep(zipcode)
        : `${fullAddress} - ${formatCep(zipcode)}`;

  if (neighborhood)
    fullAddress =
      fullAddress === ""
        ? new StringsFuncs(neighborhood).firstLetters()
        : `${fullAddress} - ${new StringsFuncs(neighborhood).capitalLetter()}`;

  if (city)
    fullAddress =
      fullAddress === ""
        ? new StringsFuncs(city).firstLetters()
        : `${fullAddress} - ${new StringsFuncs(city).capitalLetter()}`;

  return fullAddress;
};

/**
 * Formata o tamanho do arquivo em uma unidade apropriada.
 * Retorna o maior valor significativo (>= 1) arredondado.
 *
 * @param bytes - Tamanho em bytes.
 * @returns {string} Tamanho formatado com unidade (ex: "32 KB", "2 MB").
 */
export const formatFileSize = (bytes: number): string => {
  const units = ["bytes", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unitIndex = 0;

  // Converte para a maior unidade significativa
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  // Formata com 2 casas decimais e adiciona a unidade
  return `${size.toFixed(2)} ${units[unitIndex]}`;
};

/**
 * Converte uma string de números separados por ponto e vírgula
 * representando os dias da semana (1 a 7) em uma string legível.
 *
 * @param malotes - String com os dias (ex: "1;4;6") ou null/undefined.
 * @returns {string} Dias da semana separados por vírgula (ex: "Domingo, Quarta, Sexta").
 */
export const formatDiasMalote = (
  malotes: string | null | undefined
): string => {
  if (!malotes) return "-";

  const diasSemanaMap: Record<number, string> = {
    1: "Domingo",
    2: "Segunda",
    3: "Terça",
    4: "Quarta",
    5: "Quinta",
    6: "Sexta",
    7: "Sábado",
  };

  const diasFormatados = malotes
    .split(";")
    .map((dia) => diasSemanaMap[parseInt(dia.trim(), 10)])
    .filter(Boolean);

  return diasFormatados.length > 0 ? diasFormatados.join(", ") : "-";
};

/**
 * Normaliza uma string para facilitar buscas, removendo acentuação
 * e tornando tudo minúsculo. Útil para comparações case-insensitive e accent-insensitive.
 *
 * @param texto - A string de entrada a ser normalizada.
 * @returns {string} A string normalizada (sem acentos e em minúsculas).
 *
 * @example
 * normalizeString("Marquês") => "marques"
 * normalizeString("São Paulo") => "sao paulo"
 */
export const normalizeString = (texto: string): string => {
  if (!texto) return "";

  return texto
    .normalize("NFD") // separa os acentos das letras
    .replace(/[\u0300-\u036f]/g, "") // remove os acentos
    .toLowerCase(); // transforma em minúsculo
};
